"use client";
import { FC, HTMLProps, forwardRef } from "react";

// libs
import classNames from "classnames";

// components
import { Error } from "@/components/common";

// assets
import { PrimaryChecked, PrimaryUnchecked } from "@/components/assets/icons";

type RadioButtonProps = {
  id: string | number;
  label?: string;
  name: string;
  error?: string | boolean;
  value?: string | number;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  success?: boolean;
  icon?: any;
  infoContent?: any;
  iconPosition?: "left" | "right";
  additionalLabel?: string;
} & HTMLProps<HTMLInputElement>;

const RadioButton: FC<RadioButtonProps> = forwardRef<
  HTMLInputElement,
  RadioButtonProps
>(
  (
    {
      id,
      label = "",
      value,
      name,
      maxLength,
      className,
      wrapperClassName,
      labelClassName,
      containerClassName,
      disabled,
      error,
      success,
      icon,
      infoContent,
      iconPosition = "right",
      additionalLabel,
      width = "w-full",
      ...props
    },
    ref,
  ) => {
    const inputClasses = classNames(
      "w-[25px]",
      "h-[25px]",
      "px-8",
      "border",
      "text-base",
      "focus:outline-none",
      className,
      {
        // error
        "border-red-600": !!error,
        // active
        "border-transparent active:border-[#cfe0e2]": !error,
        // disabled
        "disabled:bg-zinc-100 disabled:text-zinc-400 disabled:border-none":
          !error,
        // success
        "border-emerald-600": success,
      },
    );

    return (
      <div className={classNames("relative", containerClassName, width)}>
        <div
          className={classNames(
            "input-wrapper relative w-full bg-transparent py-[1px]",
            wrapperClassName,
          )}
        >
          <label
            className={classNames(
              "flex cursor-pointer items-center justify-start gap-[6px] text-base text-xs font-bold font-normal text-black",
              labelClassName,
            )}
            htmlFor={id}
          >
            {icon && (
              <div className="check-box-icon-wrapper primary-color secondary-bg mr-[8px] h-[38px] min-h-[38px] w-[38px] min-w-[38px] rounded-[8px] p-[6px]">
                {icon}
              </div>
            )}
            <div>
              <span
                className="block text-xs font-bold text-black"
                dangerouslySetInnerHTML={{ __html: label }}
              />
              {additionalLabel && (
                <span
                  className="mt-[4px] text-sm font-normal text-black opacity-60"
                  dangerouslySetInnerHTML={{ __html: additionalLabel }}
                />
              )}
            </div>
            <div className="relative ml-auto flex">
              <input
                id={id}
                ref={ref}
                className={classNames(
                  "relative z-10 cursor-pointer pt-4 opacity-0",
                  inputClasses,
                )}
                name={name}
                disabled={disabled}
                maxLength={maxLength}
                type="radio"
                placeholder=""
                value={value || ""}
                checked={props.checked}
                {...props}
              />
              {props.checked ? (
                <div className="absolute left-0 top-0 z-0 flex h-full w-full items-center justify-center">
                  <PrimaryChecked />
                </div>
              ) : (
                <div className="absolute left-0 top-0 z-0 flex h-full w-full items-center justify-center">
                  <PrimaryUnchecked />
                </div>
              )}
            </div>
          </label>
        </div>

        {error && typeof error === "string" && <Error text={error} />}
      </div>
    );
  },
);

RadioButton.displayName = "RadioButton";

export { RadioButton };
